import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Flex,
  Image,
  ActionButton,
  TooltipTrigger,
  Tooltip
} from '@adobe/react-spectrum';
import CheckMarkCircle from '@spectrum-icons/workflow/CheckmarkCircle';
import styles from './ChildCard.module.scss';
import { useSelector } from 'react-redux';
import moment from 'moment';
import CheckMarkCircleIcon from 'app/assets/svgIcons/CheckMarkCircleIcon';
import { LegendBetaIcon } from 'app/assets/legend/LegendBetaIcon';
import { LegendPrivateBetaIcon } from 'app/assets/legend/LegendPrivateBetaIcon';
import { LegendRiskLowIcon } from 'app/assets/legend/LegendRiskLowIcon';
import { LegendRiskModerateIcon } from 'app/assets/legend/LegendRiskModerateIcon';
import { LegendRiskHighIcon } from 'app/assets/legend/LegendRiskHighIcon';
import { LegendBetaMiniIcon } from 'app/assets/legend/LegendBetaMiniIcon';
import { LegendPrivateBetaMiniIcon } from 'app/assets/legend/LegendPrivateBetaMiniIcon';

// Import icons
import RiskLowIcon from '../../assets/icons/Risk_Low.svg';
import RiskModerateIcon from '../../assets/icons/Risk_Moderate.svg';
import RiskHighIcon from '../../assets/icons/Risk_High.svg';
import ExpandIcon from '../../assets/icons/features_card_collapsed.svg';
import alertTriangle from '../../../images/alert_traingle.svg';
import AdobeIcon from '../../assets/icons/apps_icons/AdobeIcon.svg';
import FeatureCard from '../FeatureCard';
import {
  selectCollapseAll,
  selectHighlightedIssue,
  selectUnnestParentIds
} from 'app/pages/Home/slice/selectors';
import {
  isBetaLabel,
  isPrivateBetaLabel,
  isBetaOrPrivateBetaLabel,
  getReleasePhaseIcon
} from 'utils/helpers';
import {
  normalizeRiskLevelToStored,
  RISK_LEVELS,
  STATUS_VALUES,
  MOMENT_TYPES
} from 'utils/constants';
import { RiskLevelTooltipContent } from '../shared/RiskLevelTooltipContent';
import { ProductIconCompact } from '../TimelineCard/ProductIconCompact';
import {
  useReorderable,
  AFTER_LAST_DROP_TARGET
} from '../../hooks/useReorderable';
import { ToastQueue } from '@adobe/react-spectrum';

export interface ChildCardProps {
  type: string;
  card: {
    dates?: {
      targetStart?: string;
      targetEnd?: string;
      targetMonth?: string;
      targetQuarter?: string;
    };
    issueId: string;
    title: string;
    categories?: string[];
    products?: string[]; // Add products array
    links?: Array<{ type: string; reference: string }>;
    riskLevel?: string;
    priority?: string;
    labels?: string[];
    status?: string;
    lastUpdated?: string;
    reportingStatus?: string;
    reportingSummary?: string;
    relationships?: Array<{ type: string; value: string; targetDate?: string }>;
    childRelationships?: any[];
    features?: any[]; // Add features array for feature cards
    projectType?: string;
    releasePhase?: string;
    releasePhaseId?: string; // Add releasePhaseId
    showOnTimeline?: boolean; // Add showOnTimeline
  };
  isChild?: boolean;
  level?: number;
  parentPriority?: string;
  isQuarterExpanded?: boolean;
  isCompact?: boolean;
  isUnNested?: boolean;
  isSmall?: boolean;
  isLast?: boolean;
  /** When true, features can be reordered via drag-and-drop */
  canEditFeatures?: boolean;
}

const ChildCard: React.FC<ChildCardProps> = ({
  type,
  // isSmall = false,
  isLast = false,
  card,
  isChild = false,
  level = 0,
  parentPriority = '',
  isQuarterExpanded = true,
  isCompact = false,
  isUnNested = false,
  canEditFeatures = false
}) => {
  const {
    dates,
    issueId,
    title,
    categories,
    products, // Extract products from card
    links,
    riskLevel,
    priority,
    status,
    lastUpdated,
    reportingStatus,
    reportingSummary,
    relationships = [],
    childRelationships = [],
    features = [], // Use features array for feature cards
    projectType,
    releasePhase,
    releasePhaseId,
    showOnTimeline
  } = card;

  const [cardExpanded, setCardExpanded] = useState(true);
  const isCollapsed = useSelector(selectCollapseAll);

  // Feature drag-and-drop: local order state, synced from props + localStorage
  const storageKey = `roadmap:feature-order:${issueId}${
    releasePhaseId ? `:${releasePhaseId}` : ''
  }`;
  const [orderedFeatures, setOrderedFeatures] = useState<any[]>(() => {
    const stored =
      typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
    if (stored && features?.length) {
      try {
        const ids: string[] = JSON.parse(stored);
        const byId = new Map(
          features.map((f: any) => [(f.featureId || f.issueId) as string, f])
        );
        return ids.map(id => byId.get(id)).filter(Boolean) as any[];
      } catch {
        return features;
      }
    }
    return features || [];
  });

  useEffect(() => {
    const next = features || [];
    if (next.length === 0) {
      setOrderedFeatures([]);
      return;
    }
    const stored =
      typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
    if (stored) {
      try {
        const ids: string[] = JSON.parse(stored);
        const byId = new Map(
          next.map((f: any) => [(f.featureId || f.issueId) as string, f])
        );
        const ordered = ids.map(id => byId.get(id)).filter(Boolean) as any[];
        if (ordered.length === next.length) {
          setOrderedFeatures(ordered);
          return;
        }
      } catch {
        // fall through to default order
      }
    }
    setOrderedFeatures(next);
  }, [features, storageKey]);

  const [lastReorderedFeatureId, setLastReorderedFeatureId] = useState<
    string | null
  >(null);

  const handleFeaturesReorder = useCallback(
    (newOrder: any[], movedItemId?: string) => {
      try {
        const ids = newOrder.map((f: any) => f.featureId || f.issueId);
        localStorage.setItem(storageKey, JSON.stringify(ids));
        ToastQueue.positive('Features order has been changed.', {
          timeout: 3000
        });
        if (movedItemId) {
          setLastReorderedFeatureId(movedItemId);
          setTimeout(() => setLastReorderedFeatureId(null), 1200);
        }
      } catch {
        // ignore
      }
    },
    [storageKey]
  );

  const featureReorder = useReorderable(
    orderedFeatures,
    setOrderedFeatures,
    (f: any) => (f.featureId || f.issueId) as string,
    {
      disabled: !canEditFeatures,
      rowDataTestIdPrefix: 'child-card-feature-wrapper-',
      dragImageOffsetX: 16,
      dragImageOffsetY: 20,
      onPersist: handleFeaturesReorder
    }
  );
  const unnestParentIds = useSelector(selectUnnestParentIds);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isSmall = useSelector(selectCollapseAll);
  const isClosed =
    status === STATUS_VALUES.COMPLETE || status === STATUS_VALUES.CLOSED;
  const isExpandable =
    (childRelationships && childRelationships.length > 0) ||
    (features && features.length > 0);
  const isOverdue =
    dates?.targetEnd !== null
      ? dates?.targetEnd &&
        moment(dates.targetEnd).isBefore(moment(), 'day') &&
        !isClosed
      : dates?.targetStart &&
        moment(dates.targetStart).isBefore(moment(), 'day') &&
        !isClosed;

  // Add tooltip state management similar to TimelineCard
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Add tooltip handlers similar to TimelineCard
  const handleTooltipMouseEnter = (key: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setOpenTooltip(key);
    }, 1000);
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setOpenTooltip(null);
  };

  // Find the parent relationship that matches this child's parent
  const parentRel = relationships?.find(rel => rel.type === 'is child of');

  // Check if dates match
  const datesMatch = dates?.targetStart === parentRel?.targetDate;

  const highlightedIssue = useSelector(selectHighlightedIssue);
  const isHighlighted = !!highlightedIssue[issueId];

  // Cleanup tooltip timer on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  // Add effect to handle collapse all
  useEffect(() => {
    if (isSmall) {
      setCardExpanded(false);
    }
  }, [isSmall]);

  React.useEffect(() => {
    if (isCompact) {
      setCardExpanded(false);
      if (isExpandable && !isChild) {
        setCardExpanded(false);
      }
    }
  }, [isCompact, isExpandable, isChild]);

  React.useEffect(() => {
    if (isCollapsed) {
      setCardExpanded(false);
    }
  }, [isCollapsed]);

  // Handle effects for different states
  // Add effect to expand cards in un-nested mode or when in unnestParentIds (from search/filters)
  React.useEffect(() => {
    if (isExpandable) {
      // Check if we're in filter mode (search/events with specific parents to unnest)
      if (unnestParentIds && unnestParentIds.length > 0) {
        // Create composite key using issueId, releasePhaseId, and showOnTimeline
        const compositeKey = releasePhaseId
          ? `${issueId}-${releasePhaseId}-${showOnTimeline}`
          : issueId;

        // Check if this card should be expanded due to search/filter results
        // Check both composite key and plain issueId for backwards compatibility
        const shouldExpand =
          unnestParentIds.includes(compositeKey) ||
          unnestParentIds.includes(issueId);

        setCardExpanded(shouldExpand);
      } else {
        // Global mode: follow isUnNested flag
        setCardExpanded(isUnNested);
      }
    }
  }, [
    isUnNested,
    isExpandable,
    unnestParentIds,
    issueId,
    releasePhaseId,
    showOnTimeline
  ]);

  // Add effect to handle un-nesting in collapsed mode
  useEffect(() => {
    if (isSmall && isUnNested && isExpandable) {
      setCardExpanded(true);
    }
  }, [isSmall, isUnNested, isExpandable]);

  const getStatusIcon = () => {
    const size = isCompact || isSmall ? 20 : 22;
    const normalizedRiskLevel = normalizeRiskLevelToStored(riskLevel);
    if (normalizedRiskLevel === RISK_LEVELS.LOW) {
      return <LegendRiskLowIcon width={size} height={size} />;
    } else if (normalizedRiskLevel === RISK_LEVELS.MODERATE) {
      return <LegendRiskModerateIcon width={size} height={size} />;
    } else if (normalizedRiskLevel === RISK_LEVELS.HIGH) {
      return <LegendRiskHighIcon width={size} height={size} />;
    }
    return null;
  };

  const getPriorityLevel = (priority: string) => {
    if (!priority) return 0;
    if (priority.includes('L1')) return 1;
    if (priority.includes('L2')) return 2;
    if (priority.includes('L3')) return 3;
    return 0;
  };

  const shouldShowPriority = () => {
    if (!isChild || !priority) return true;
    const currentPriorityLevel = getPriorityLevel(priority);
    const parentPriorityLevel = getPriorityLevel(parentPriority);
    if (currentPriorityLevel === 0 || parentPriorityLevel === 0) return true;
    return currentPriorityLevel > parentPriorityLevel;
  };

  const showPriority = shouldShowPriority();

  const openDocumentLink = () => {
    const docs = links?.filter(link => link.type === 'DOCUMENTATION');
    if (docs && docs.length > 0 && docs[0]?.reference) {
      window.open(docs[0].reference, '_blank');
    }
  };

  const getChildSpacing = (index: number, child?: any) => {
    // Special spacing for Feature cards to match Figma 10px gap
    if (
      child?.projectType === MOMENT_TYPES.FEATURE ||
      features?.some(f => f.issueId === child?.issueId)
    ) {
      return {
        position: 'relative' as const,
        paddingTop: index === 0 ? '0px' : '10px', // No padding for first feature card, 10px gap for subsequent ones
        marginBottom:
          index === (features?.length || childRelationships.length) - 1
            ? '0'
            : '0' // Let CSS handle bottom spacing
      };
    }

    // Regular spacing for normal child cards
    return {
      position: 'relative' as const,
      paddingTop: isSmall
        ? index === 0
          ? '0.75rem'
          : '0.3rem'
        : isQuarterExpanded
        ? '0.75rem'
        : index === 0
        ? '0.75rem'
        : '0.3rem',
      marginBottom: isSmall || isCollapsed ? '0.1rem' : '0.3rem'
    };
  };

  // Add refs for SVG feature connections
  const featureContainerRef = useRef<HTMLDivElement>(null);
  const [featureConnectorPaths, setFeatureConnectorPaths] = useState<string[]>(
    []
  );

  // Calculate SVG paths for feature connections
  const calculateFeatureConnectorPaths = useCallback(() => {
    if (!contentRef.current || !featureContainerRef.current || !cardExpanded)
      return;

    const containerRect = featureContainerRef.current.getBoundingClientRect();
    const parentRect = contentRef.current.getBoundingClientRect();

    const parentRight = parentRect.right - containerRect.left;
    const parentCenterY =
      parentRect.top - containerRect.top + parentRect.height / 2;
    const verticalLineX = parentRight + 5; // Position of vertical line

    // Use a more specific selector that only matches the main feature card divs
    const featureElements = featureContainerRef.current.querySelectorAll(
      'div[data-testid^="feature-card-"]:not([data-testid*="checkmark"]):not([data-testid*="title"]):not([data-testid*="risk-level"]):not([data-testid*="label"]):not([data-testid*="overdue"])'
    );

    // Only proceed if we have feature elements
    if (featureElements.length === 0) return;

    const paths: string[] = [];

    // Calculate the vertical line extent and get last feature Y position
    let maxY = -Infinity;
    let lastFeatureCenterY = 0;
    let firstFeatureCenterY = Infinity;
    featureElements.forEach(featureEl => {
      const featureRect = featureEl.getBoundingClientRect();
      const featureCenterY =
        featureRect.top - containerRect.top + featureRect.height / 2;
      maxY = Math.max(maxY, featureCenterY);
      lastFeatureCenterY = featureCenterY;
      firstFeatureCenterY = Math.min(firstFeatureCenterY, featureCenterY);
    });

    // Draw the background vertical line first to fill any gaps
    // Start a bit after parent and end a bit before last feature
    const verticalPadding = 4; // Increased padding to make line shorter
    paths.push(
      `
      M ${verticalLineX} ${parentCenterY + verticalPadding}
      L ${verticalLineX} ${lastFeatureCenterY - verticalPadding}
    `.trim()
    );

    // 1. Draw the parent connection and main vertical line segments
    const curveRadius = 5; // Small radius for subtle curve

    // Start with parent connection
    paths.push(
      `
      M ${parentRight} ${parentCenterY}
      L ${verticalLineX - curveRadius} ${parentCenterY}
      Q ${verticalLineX} ${parentCenterY}, ${verticalLineX} ${
        parentCenterY + curveRadius
      }
    `.trim()
    );

    // Draw vertical line segments between features with curves
    let prevY = parentCenterY + curveRadius;
    featureElements.forEach((featureEl, index) => {
      const featureRect = featureEl.getBoundingClientRect();
      const featureCenterY =
        featureRect.top - containerRect.top + featureRect.height / 2;

      // Draw vertical line segment up to this feature
      paths.push(
        `
        M ${verticalLineX} ${prevY}
        L ${verticalLineX} ${featureCenterY - curveRadius}
        Q ${verticalLineX} ${featureCenterY}, ${
          verticalLineX - curveRadius
        } ${featureCenterY}
      `.trim()
      );

      prevY = featureCenterY + curveRadius;
    });

    // 2. Draw individual connections to each feature card
    featureElements.forEach(featureEl => {
      const featureRect = featureEl.getBoundingClientRect();
      const featureCenterY =
        featureRect.top - containerRect.top + featureRect.height / 2;
      const featureRight = featureRect.right - containerRect.left;

      // Draw horizontal line starting after the curve
      const path = `
        M ${verticalLineX - curveRadius} ${featureCenterY}
        H ${featureRight}
      `.trim();
      paths.push(path);
    });

    setFeatureConnectorPaths(paths);
  }, [cardExpanded, childRelationships]);

  // Update feature connector paths on resize or expand/collapse
  useEffect(() => {
    // Wait for next frame to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      calculateFeatureConnectorPaths();
    }, 0);

    const observer = new ResizeObserver(() => {
      calculateFeatureConnectorPaths();
    });

    if (featureContainerRef.current) {
      observer.observe(featureContainerRef.current);
    }
    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [calculateFeatureConnectorPaths, cardExpanded]);

  return (
    <React.Fragment>
      <div
        ref={contentRef}
        data-testid={`child-card-${issueId}`}
        className={`${styles.card} ${isClosed ? styles.closed : ''} ${
          isChild ? styles.child : ''
        }`}
        style={{
          marginLeft: isCollapsed && level > 0 ? `${level * 1}rem` : '0',
          marginBottom: isLast ? '-0.9rem' : '0',
          cursor: 'pointer',
          position: 'relative'
        }}
      >
        <View
          UNSAFE_className={styles.cardLink}
          data-testid={`child-card-link-${issueId}`}
        >
          <Flex direction="row" height={isCollapsed ? 'size-400' : 'size-700'}>
            <Flex
              direction={isChild ? (isCollapsed ? 'row' : 'column') : 'column'}
              alignItems={isChild && isCollapsed ? 'center' : 'start'}
              height={isCollapsed ? 'size-400' : 'size-800'}
              flex={1}
              UNSAFE_className={`${styles.content} ${
                isCompact || isCollapsed ? styles.compact : styles.expanded
              }`}
              data-testid={`child-card-content-${issueId}`}
            >
              <div
                ref={containerRef}
                className={styles.titleContainer}
                data-testid={`child-card-title-container-${issueId}`}
              >
                <TooltipTrigger
                  isOpen={openTooltip === 'title'}
                  onOpenChange={isOpen => {
                    if (!isOpen) setOpenTooltip(null);
                  }}
                  placement="top"
                  offset={6}
                >
                  <div
                    className={styles.tooltipWrapper}
                    onMouseEnter={() => handleTooltipMouseEnter('title')}
                    onMouseLeave={handleTooltipMouseLeave}
                    style={{ width: '100%' }}
                  >
                    <ActionButton
                      data-testid={`child-card-title-button-${issueId}`}
                      isQuiet
                      UNSAFE_className={`${styles.heading} ${
                        isChild ? styles.main : ''
                      } ${isSmall ? styles.compact : ''} ${
                        isHighlighted ? styles.highlighted : ''
                      }`}
                      onPress={openDocumentLink}
                      UNSAFE_style={
                        isCompact || isSmall
                          ? {
                              border: 'none',
                              backgroundColor: 'transparent',
                              outline: 'none',
                              color: isHighlighted ? '#147af3' : undefined,
                              flex: '1 1 7rem',
                              maxWidth: '100%',
                              width: 'auto',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              height: 'fit-content'
                            }
                          : {
                              border: 'none',
                              backgroundColor: 'transparent',
                              outline: 'none',
                              color: isHighlighted ? '#147af3' : undefined,
                              height: 'fit-content'
                            }
                      }
                    >
                      <span
                        className={styles.headingText}
                        style={isHighlighted ? { color: '#147af3' } : {}}
                      >
                        {title}
                      </span>
                    </ActionButton>
                  </div>
                  <Tooltip>{title}</Tooltip>
                </TooltipTrigger>
              </div>

              <div
                className={`${styles.iconsContainer} ${
                  !isCompact && !isCollapsed && !isSmall ? styles.large : ''
                } ${isCompact || isCollapsed || isSmall ? styles.compact : ''}`}
                data-testid={`child-card-icons-container-${issueId}`}
              >
                {!datesMatch && dates?.targetStart && (
                  <span className={styles.priorityBadge}>
                    {moment(dates.targetStart)
                      .format('DD MMM')
                      .toLocaleUpperCase()}
                  </span>
                )}

                {showPriority && priority && !isClosed && (
                  <span
                    className={styles.priorityBadge}
                    data-testid={`child-card-priority-${issueId}`}
                  >
                    {priority}
                  </span>
                )}

                {/* Icon order from right: Status, Product, P/PB, Expand */}

                {/* Expand/unnest icon */}
                {isExpandable && (
                  <ActionButton
                    data-testid={`child-card-expand-button-${issueId}`}
                    height="size-225"
                    aria-label="Expand"
                    onPress={() => setCardExpanded(!cardExpanded)}
                    isQuiet
                    UNSAFE_className={styles.expandButton}
                  >
                    <Image height="size-225" src={ExpandIcon} alt="" />
                  </ActionButton>
                )}

                {/* P/PB icons (phase badges) if present */}
                {releasePhase && getReleasePhaseIcon(releasePhase) && (
                  <TooltipTrigger delay={700} placement="top">
                    <ActionButton
                      data-testid={`child-card-label-${getReleasePhaseIcon(
                        releasePhase
                      )}-${issueId}`}
                      isQuiet
                      UNSAFE_className={styles.iconButtonReset}
                      aria-label={releasePhase}
                    >
                      {getReleasePhaseIcon(releasePhase) === 'beta' ? (
                        <LegendBetaMiniIcon />
                      ) : (
                        <LegendPrivateBetaMiniIcon />
                      )}
                    </ActionButton>
                    <Tooltip>{releasePhase}</Tooltip>
                  </TooltipTrigger>
                )}

                {/* Product Icon - shows first product with + indicator for multi-product */}
                {products && products.length > 0 && (
                  <TooltipTrigger delay={700} placement="top">
                    <ActionButton
                      isQuiet
                      UNSAFE_className={styles.iconButtonReset}
                      aria-label="Product Icon"
                    >
                      <ProductIconCompact products={products} />
                    </ActionButton>
                    <Tooltip>
                      {Array.isArray(products)
                        ? products.length > 1
                          ? `${products[0]} +${products.length - 1} more`
                          : products[0]
                        : products}
                    </Tooltip>
                  </TooltipTrigger>
                )}

                {/* Risk Level (status) - rightmost */}
                {riskLevel && riskLevel !== 'NA' && !isClosed && (
                  <TooltipTrigger delay={1000} placement="top">
                    <ActionButton
                      data-testid={`child-card-risk-level-${issueId}`}
                      isQuiet
                      UNSAFE_className={styles.iconButtonReset}
                      aria-label={`Risk Level: ${riskLevel}`}
                    >
                      {getStatusIcon()}
                    </ActionButton>
                    <Tooltip>
                      <RiskLevelTooltipContent
                        riskLevel={riskLevel}
                        className={styles.riskLevelText}
                      />
                    </Tooltip>
                  </TooltipTrigger>
                )}
              </div>
            </Flex>
          </Flex>
        </View>

        {isClosed && (
          <div
            className={styles.checkmark}
            data-testid={`child-card-checkmark-${issueId}`}
          >
            <CheckMarkCircleIcon
              width={isCollapsed && isChild ? '16' : '18'}
              height={isCollapsed && isChild ? '16' : '18'}
            />
          </div>
        )}

        {isOverdue && (
          <div
            className={styles.alert}
            data-testid={`child-card-overdue-${issueId}`}
          >
            <Image
              src={alertTriangle}
              height={isCollapsed && isChild ? '18px' : '20px'}
              width={isCollapsed && isChild ? '18px' : '20px'}
            />
          </div>
        )}
      </div>

      {cardExpanded && (
        <div
          className={styles.childrenContainer}
          data-testid={`child-card-children-container-${issueId}`}
          style={{ position: 'relative' }}
        >
          {/* Left-side container for regular child cards */}
          {childRelationships.some(
            child => child.projectType !== MOMENT_TYPES.FEATURE
          ) && (
            <div
              style={{
                borderLeft: '2px solid #96cefd',
                paddingLeft: '0.2rem',
                marginTop: '-12px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {childRelationships
                .filter(child => child.projectType !== MOMENT_TYPES.FEATURE)
                .map((child, index, filteredChildren) => (
                  <div
                    key={child.issueId}
                    style={getChildSpacing(index, child)}
                    data-testid={`child-card-child-wrapper-${child.issueId}`}
                  >
                    <ChildCard
                      type={type}
                      card={child}
                      isChild={true}
                      level={level + 1}
                      parentPriority={priority}
                      isQuarterExpanded={isQuarterExpanded}
                      isCompact={isCompact}
                      isLast={index === filteredChildren.length - 1}
                      isUnNested={isUnNested}
                      canEditFeatures={canEditFeatures}
                    />
                  </div>
                ))}
            </div>
          )}

          {/* Right-side container for feature cards */}
          {features.length > 0 && (
            <div
              ref={featureContainerRef}
              style={{
                position: 'relative',
                paddingTop: '23px',
                marginTop: '-12px',
                display: 'flex',
                flexDirection: 'column',
                marginLeft: 'auto' // Push to the right side
              }}
            >
              {/* SVG container for curved connections */}
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  overflow: 'visible',
                  zIndex: -1 // Place SVG behind the cards
                }}
              >
                {featureConnectorPaths.map((path, index) => (
                  <path
                    key={index}
                    d={path}
                    stroke="#96cefd"
                    strokeWidth="2"
                    fill="none"
                  />
                ))}
              </svg>

              {orderedFeatures.map((feature, index, filteredFeatures) => {
                const childUniqueId = feature.featureId || feature.issueId;
                const isDragging = featureReorder.draggedId === childUniqueId;
                const isDragOver = featureReorder.dragOverId === childUniqueId;
                const isJustReordered =
                  lastReorderedFeatureId === childUniqueId;
                const showDropLineAbove =
                  featureReorder.dropPosition?.index === index;
                const showDropLineBelow =
                  featureReorder.dropPosition?.index === index + 1;
                return (
                  <div
                    key={childUniqueId}
                    data-testid={`child-card-feature-wrapper-${childUniqueId}`}
                    onDragOver={
                      canEditFeatures
                        ? e =>
                            featureReorder.handleDragOver(
                              e,
                              childUniqueId,
                              orderedFeatures
                            )
                        : undefined
                    }
                    onDragLeave={
                      canEditFeatures
                        ? e => featureReorder.handleDragLeave(e, childUniqueId)
                        : undefined
                    }
                    onDrop={
                      canEditFeatures
                        ? e =>
                            featureReorder.handleDrop(
                              e,
                              childUniqueId,
                              orderedFeatures
                            )
                        : undefined
                    }
                    style={{
                      ...getChildSpacing(index, feature),
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'stretch',
                      justifyContent: 'flex-end',
                      zIndex: isDragging ? 10 : 1
                    }}
                    className={`${styles.featureRow} ${
                      isDragOver ? styles.featureDragOver : ''
                    }`.trim()}
                  >
                    {showDropLineAbove && (
                      <div
                        className={styles.featureDropIndicator}
                        style={{ top: 0, left: 0, right: 0 }}
                      />
                    )}
                    <div
                      draggable={canEditFeatures}
                      onDragStart={
                        canEditFeatures
                          ? e =>
                              featureReorder.handleDragStart(e, childUniqueId)
                          : undefined
                      }
                      onDragEnd={featureReorder.handleDragEnd}
                      onKeyDown={
                        canEditFeatures
                          ? e =>
                              featureReorder.handleRowKeyDown(
                                e,
                                childUniqueId,
                                orderedFeatures
                              )
                          : undefined
                      }
                      style={{
                        flex: 1,
                        minWidth: 0,
                        transition:
                          'opacity 0.25s cubic-bezier(0.25, 0.4, 0.25, 1)',
                        cursor: canEditFeatures ? 'grab' : 'default'
                      }}
                      className={isDragging ? styles.featurePlaceholder : ''}
                    >
                      {isDragging ? (
                        <div className={styles.featurePlaceholderInner} />
                      ) : (
                        <FeatureCard
                          type={type}
                          card={{
                            ...feature,
                            parentReleasePhase: releasePhase,
                            parentDates: dates
                          }}
                          isChild={true}
                          level={2}
                          parentPriority={priority}
                          isQuarterExpanded={isQuarterExpanded}
                          isLast={index === filteredFeatures.length - 1}
                          isJustReordered={isJustReordered}
                        />
                      )}
                    </div>
                    {showDropLineBelow && (
                      <div
                        className={styles.featureDropIndicator}
                        style={{ bottom: 0, left: 0, right: 0 }}
                      />
                    )}
                  </div>
                );
              })}

              {canEditFeatures && orderedFeatures.length > 0 && (
                <div
                  data-testid={`child-card-feature-wrapper-${AFTER_LAST_DROP_TARGET}`}
                  onDragOver={e =>
                    featureReorder.handleDragOver(
                      e,
                      AFTER_LAST_DROP_TARGET,
                      orderedFeatures
                    )
                  }
                  onDragLeave={e =>
                    featureReorder.handleDragLeave(e, AFTER_LAST_DROP_TARGET)
                  }
                  onDrop={e =>
                    featureReorder.handleDrop(
                      e,
                      AFTER_LAST_DROP_TARGET,
                      orderedFeatures
                    )
                  }
                  className={styles.featureAfterLastDropZone}
                  style={{
                    minHeight:
                      featureReorder.dragOverId === AFTER_LAST_DROP_TARGET
                        ? 24
                        : 12
                  }}
                >
                  {featureReorder.dragOverId === AFTER_LAST_DROP_TARGET &&
                    featureReorder.dropPosition?.index ===
                      orderedFeatures.length && (
                      <div
                        className={styles.featureDropIndicator}
                        style={{ top: 0, left: 0, right: 0 }}
                      />
                    )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default ChildCard;
